<template>
  <div class="markdown-email-editor border rounded">
    <div
      class="editor-toolbar border-bottom p-2 d-flex flex-wrap gap-2 align-items-center"
      role="toolbar"
      aria-label="Email formatting"
    >
      <div class="btn-group btn-group-sm" role="group" aria-label="Editing mode">
        <button
          type="button"
          class="btn btn-outline-secondary"
          :class="{ active: mode === 'visual' }"
          :disabled="disabled || uploading"
          @click="mode = 'visual'"
        >
          Visual
        </button>
        <button
          type="button"
          class="btn btn-outline-secondary"
          :class="{ active: mode === 'markdown' }"
          :disabled="disabled || uploading"
          @click="mode = 'markdown'"
        >
          Markdown
        </button>
      </div>

      <template v-if="mode === 'visual' && editor">
        <select
          class="form-select form-select-sm editor-select"
          aria-label="Text style"
          :value="editor.getAttributes('heading').level || 0"
          :disabled="disabled || uploading"
          @change="setHeading($event.target.value)"
        >
          <option value="0">Paragraph</option>
          <option value="1">Heading 1</option>
          <option value="2">Heading 2</option>
          <option value="3">Heading 3</option>
        </select>

        <div class="btn-group btn-group-sm" role="group" aria-label="Text formatting">
          <button
            v-for="item in formattingButtons"
            :key="item.command"
            type="button"
            class="btn btn-outline-secondary"
            :title="item.label"
            :aria-label="item.label"
            :aria-pressed="editor.isActive(item.active)"
            :class="{ active: editor.isActive(item.active) }"
            :disabled="disabled || uploading"
            @click="runCommand(item.command)"
          >
            <i :class="item.icon" aria-hidden="true" />
          </button>
        </div>

        <div class="btn-group btn-group-sm" role="group" aria-label="Text alignment">
          <button
            v-for="item in alignmentButtons"
            :key="item.value"
            type="button"
            class="btn btn-outline-secondary"
            :title="item.label"
            :aria-label="item.label"
            :aria-pressed="textAlignment === item.value"
            :class="{ active: textAlignment === item.value }"
            :disabled="disabled || uploading"
            @click="setAlignment(item.value)"
          >
            <i :class="item.icon" aria-hidden="true" />
          </button>
        </div>

        <button
          type="button"
          class="btn btn-sm btn-outline-secondary"
          :disabled="disabled || uploading"
          @click="showLinkInput = !showLinkInput"
        >
          Link
        </button>
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary"
          :disabled="disabled || uploading"
          @click="insertTable"
        >
          Table
        </button>
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary"
          :disabled="disabled || uploading"
          @click="$refs.imageInput.click()"
        >
          {{ uploading ? 'Preparing image…' : 'Insert image' }}
        </button>
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary"
          :disabled="disabled || uploading"
          @click="showImageUrlInput = !showImageUrlInput"
        >
          Image URL
        </button>
        <select
          class="form-select form-select-sm editor-select"
          aria-label="Insert license field"
          :disabled="disabled || uploading"
          @change="insertField($event)"
        >
          <option value="">Insert field…</option>
          <option v-for="field in fields" :key="field.name" :value="field.name">
            {{ field.label }}
          </option>
        </select>
      </template>
    </div>

    <div v-if="showLinkInput && mode === 'visual'" class="d-flex gap-2 p-2 border-bottom">
      <input
        v-model="linkUrl"
        class="form-control form-control-sm"
        type="url"
        aria-label="Link URL"
        placeholder="https://example.gov"
        :disabled="disabled || uploading"
        @keydown.enter.prevent="insertLink"
      />
      <button
        type="button"
        class="btn btn-sm btn-primary"
        :disabled="disabled || uploading"
        @click="insertLink"
      >
        Apply link
      </button>
      <button
        type="button"
        class="btn btn-sm btn-outline-secondary"
        :disabled="disabled || uploading"
        @click="removeLink"
      >
        Remove link
      </button>
    </div>

    <div v-if="showImageUrlInput && mode === 'visual'" class="d-flex flex-wrap gap-2 p-2 border-bottom">
      <input
        v-model="imageUrl"
        class="form-control form-control-sm"
        type="url"
        aria-label="Image URL"
        placeholder="https://example.gov/banner.png"
        :disabled="disabled || uploading"
        @keydown.enter.prevent="insertImageUrl"
      />
      <input
        v-model="imageDescription"
        class="form-control form-control-sm"
        aria-label="Image description"
        placeholder="Description (optional)"
        :disabled="disabled || uploading"
        @keydown.enter.prevent="insertImageUrl"
      />
      <button
        type="button"
        class="btn btn-sm btn-primary"
        :disabled="disabled || uploading"
        @click="insertImageUrl"
      >
        Insert URL image
      </button>
    </div>

    <div
      v-if="mode === 'visual' && editor?.isActive('table')"
      class="d-flex flex-wrap gap-2 p-2 border-bottom"
    >
      <button
        v-for="item in tableButtons"
        :key="item.command"
        type="button"
        class="btn btn-sm btn-outline-secondary"
        :disabled="disabled || uploading"
        @click="runCommand(item.command)"
      >
        {{ item.label }}
      </button>
    </div>

    <div v-if="error" class="alert alert-danger m-2" role="alert">{{ error }}</div>
    <EditorContent v-show="mode === 'visual'" :editor="editor" />
    <textarea
      v-if="mode === 'markdown'"
      class="form-control border-0 rounded-0 font-monospace"
      rows="14"
      aria-label="Email Markdown source"
      :value="modelValue"
      :disabled="disabled || uploading"
      :maxlength="maxLength"
      @input="$emit('update:modelValue', $event.target.value)"
    />
    <input
      ref="imageInput"
      type="file"
      class="d-none"
      accept="image/png,image/jpeg"
      @change="uploadImage"
    />
    <div class="small text-muted border-top p-2">
      {{ modelValue.length.toLocaleString() }} / {{ maxLength.toLocaleString() }} characters. Use
      Preview email to see the final layout with license details.
    </div>
  </div>
</template>

<script>
import { markRaw } from 'vue';
import { Editor, EditorContent } from '@tiptap/vue-3';
import Image from '@tiptap/extension-image';
import { TableKit } from '@tiptap/extension-table';
import TextAlign from '@tiptap/extension-text-align';
import { Markdown } from '@tiptap/markdown';
import { createEmailStarterKit } from './emailEditorExtensions';
import {
  emailTemplateImageId,
  embeddedEmailImageId,
  embeddedEmailImageBytes,
  validateEmbeddedEmailImages,
  EMAIL_TEMPLATE_IMAGE_STORAGE_MODE,
  LICENSE_STATUS_EMAIL_FIELDS,
  LICENSE_STATUS_EMAIL_MARKDOWN_MAX_LENGTH
} from '@strenforcement/domain/licenseStatusEmail';
import { createEmbeddedEmailImage, loadEmailImage, uploadEmailImage } from '@/emailTemplateImages';

export default {
  components: { EditorContent },
  props: {
    modelValue: { type: String, default: '' },
    cityId: { type: String, required: true },
    images: { type: Object, default: () => ({}) },
    imageStorageMode: {
      type: String,
      default: EMAIL_TEMPLATE_IMAGE_STORAGE_MODE,
      validator: value => ['embedded', 'storage'].includes(value)
    },
    disabled: { type: Boolean, default: false }
  },
  emits: ['update:modelValue', 'update:images', 'busy'],
  data() {
    return {
      editor: null,
      mode: 'visual',
      uploading: false,
      error: null,
      showLinkInput: false,
      linkUrl: '',
      showImageUrlInput: false,
      imageUrl: '',
      imageDescription: '',
      imageCache: markRaw(new Map()),
      embeddedImages: { ...this.images },
      fields: LICENSE_STATUS_EMAIL_FIELDS,
      maxLength: LICENSE_STATUS_EMAIL_MARKDOWN_MAX_LENGTH,
      formattingButtons: [
        { command: 'toggleBold', active: 'bold', label: 'Bold', icon: 'bi-type-bold' },
        { command: 'toggleItalic', active: 'italic', label: 'Italic', icon: 'bi-type-italic' },
        { command: 'toggleUnderline', active: 'underline', label: 'Underline', icon: 'bi-type-underline' },
        {
          command: 'toggleBlockquote',
          active: 'blockquote',
          label: 'Box paragraph',
          icon: 'bi-border-outer'
        },
        {
          command: 'toggleBulletList',
          active: 'bulletList',
          label: 'Bullet list',
          icon: 'bi-list-ul'
        },
        {
          command: 'toggleOrderedList',
          active: 'orderedList',
          label: 'Numbered list',
          icon: 'bi-list-ol'
        }
      ],
      alignmentButtons: [
        { value: 'left', label: 'Align left', icon: 'bi-text-left' },
        { value: 'center', label: 'Align center', icon: 'bi-text-center' },
        { value: 'right', label: 'Align right', icon: 'bi-text-right' },
        { value: 'justify', label: 'Justify', icon: 'bi-justify' }
      ],
      tableButtons: [
        { command: 'addRowAfter', label: 'Add row' },
        { command: 'addColumnAfter', label: 'Add column' },
        { command: 'deleteRow', label: 'Remove row' },
        { command: 'deleteColumn', label: 'Remove column' },
        { command: 'deleteTable', label: 'Remove table' }
      ]
    };
  },
  computed: {
    /** Reflects the current paragraph or heading, with left alignment as the default. */
    textAlignment() {
      return this.editor?.getAttributes('paragraph').textAlign
        || this.editor?.getAttributes('heading').textAlign
        || 'left';
    }
  },
  watch: {
    /** Retains embedded bytes through saves, resets, and panel collapse/reopening. */
    images(value) {
      const changed = Object.entries(value).some(([id, dataUrl]) => this.embeddedImages[id] !== dataUrl);
      this.embeddedImages = { ...this.embeddedImages, ...value };
      if (!changed) return;

      this.editor?.commands.setContent(this.modelValue || '', {
        contentType: 'markdown',
        emitUpdate: false
      });
    },

    /** Replaces saved/reset content without creating another update event. */
    modelValue(value) {
      if (this.editor && this.editor.getMarkdown() !== value) {
        this.editor.commands.setContent(value || '', {
          contentType: 'markdown',
          emitUpdate: false
        });
      }
    },

    /** Drops previews from the previous municipality when the surrounding admin page changes. */
    cityId() {
      this.imageCache.clear();
      this.embeddedImages = { ...this.images };
      this.editor?.commands.setContent(this.modelValue || '', {
        contentType: 'markdown',
        emitUpdate: false
      });
    },

    /** Keeps editor keyboard input disabled while the settings are being saved. */
    disabled(value) {
      this.editor?.setEditable(!value && !this.uploading, false);
    }
  },

  mounted() {
    const component = this;
    const emailImage = Image.extend({
      /** Displays authenticated images while retaining their stable reference in Markdown. */
      addNodeView() {
        return ({ node }) => {
          const image = document.createElement('img');
          image.alt = node.attrs.alt || 'Email image';

          if (emailTemplateImageId(node.attrs.src) || embeddedEmailImageId(node.attrs.src)) {
            component
              .imageSource(node.attrs.src)
              .then((source) => {
                image.src = source;
              })
              .catch((error) => {
                component.error = error.message || 'Unable to load an email image.';
              });
          } else if (/^https:\/\//i.test(node.attrs.src || '')) {
            image.src = node.attrs.src;
          }

          return { dom: image };
        };
      }
    });

    this.editor = markRaw(
      new Editor({
        extensions: [
          createEmailStarterKit(() => this.editor).configure({
            heading: { levels: [1, 2, 3] },
            link: { openOnClick: false }
          }),
          TextAlign.configure({ types: ['paragraph', 'heading'] }),
          emailImage,
          TableKit,
          Markdown
        ],
        content: this.modelValue || '',
        contentType: 'markdown',
        editable: !this.disabled,
        editorProps: {
          attributes: {
            role: 'textbox',
            'aria-label': 'Custom email body',
            'aria-multiline': 'true'
          }
        },
        onUpdate: ({ editor }) => this.publishContent(editor)
      })
    );
  },

  beforeUnmount() {
    this.editor?.destroy();
    this.$emit('busy', false);
  },

  methods: {
    /** Keeps only currently used images in the saved template; local bytes remain available to undo. */
    referencedImages(editor = this.editor) {
      const images = {};
      editor.state.doc.descendants(node => {
        if (node.type.name !== 'image') return;

        const id = embeddedEmailImageId(node.attrs.src);
        if (id && this.embeddedImages[id]) images[id] = this.embeddedImages[id];
      });

      return images;
    },

    /** Publishes Markdown and its embedded bytes together without resetting selection on each keystroke. */
    publishContent(editor) {
      const images = this.referencedImages(editor);
      if (JSON.stringify(images) !== JSON.stringify(this.images)) {
        this.$emit('update:images', images);
      }

      this.$emit('update:modelValue', editor.getMarkdown());
    },

    /** Runs a toolbar action at the editor's current selection. */
    runCommand(command) {
      this.editor.chain().focus()[command]().run();
    },

    /** Sets the selected text blocks' alignment, removing the override for ordinary left alignment. */
    setAlignment(value) {
      const chain = this.editor.chain().focus();

      if (value === 'left') chain.unsetTextAlign().run();
      else chain.setTextAlign(value).run();
    },

    /** Changes the current paragraph's heading level. */
    setHeading(value) {
      const chain = this.editor.chain().focus();
      if (Number(value)) chain.setHeading({ level: Number(value) }).run();
      else chain.setParagraph().run();
    },

    /** Inserts a literal placeholder without treating its value as Markdown. */
    insertField(event) {
      if (!event.target.value) return;

      this.editor
        .chain()
        .focus()
        .insertContent({
          type: 'text',
          text: `{{${event.target.value}}}`
        })
        .run();
      event.target.value = '';
    },

    /** Adds an editable two-column table for labels and registration details. */
    insertTable() {
      this.editor.chain().focus().insertTable({ rows: 3, cols: 2, withHeaderRow: true }).run();
    },

    /** Accepts ordinary web and email links for the selected text. */
    insertLink() {
      const href = this.linkUrl.trim();

      if (!/^(https?:\/\/|mailto:)/i.test(href)) {
        this.error = 'Use an HTTP, HTTPS, or mailto link.';
        return;
      }

      if (this.editor.state.selection.empty && !this.editor.isActive('link')) {
        this.editor.chain().focus().insertContent({
          type: 'text',
          text: href,
          marks: [{ type: 'link', attrs: { href } }]
        }).run();
      } else {
        this.editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
      }

      this.showLinkInput = false;
      this.error = null;
    },

    /** Removes a link without deleting the selected text. */
    removeLink() {
      this.editor.chain().focus().extendMarkRange('link').unsetLink().run();
      this.showLinkInput = false;
    },

    /** Inserts an externally hosted image without uploading or embedding its contents. */
    insertImageUrl() {
      let source;

      try {
        const url = new URL(this.imageUrl.trim());
        if (url.protocol !== 'https:' || url.username || url.password || this.imageUrl.includes('{{')) {
          throw new Error('Unsupported image URL');
        }

        source = url.href;
      } catch {
        this.error = 'Use a complete HTTPS image URL without credentials or template fields.';
        return;
      }

      this.editor.chain().focus().setImage({
        src: source,
        alt: this.imageDescription.trim()
      }).run();

      this.showImageUrlInput = false;
      this.imageUrl = '';
      this.imageDescription = '';
      this.error = null;
    },

    /** Shares image requests between repeated nodes within this editor. */
    imageSource(source) {
      const id = embeddedEmailImageId(source);
      if (id) {
        const dataUrl = this.embeddedImages[id];
        return embeddedEmailImageBytes(dataUrl) !== null
          ? Promise.resolve(dataUrl)
          : Promise.reject(new Error('An embedded image is missing or invalid. Please insert it again.'));
      }

      if (!this.imageCache.has(source)) {
        this.imageCache.set(source, loadEmailImage(this.cityId, source));
      }

      return this.imageCache.get(source);
    },

    /** Inserts an embedded or uploaded image according to the configured storage mode. */
    async uploadImage(event) {
      const file = event.target.files?.[0];
      if (!file) return;

      this.error = null;
      this.uploading = true;
      this.editor.setEditable(false, false);
      this.$emit('busy', true);

      try {
        const image = this.imageStorageMode === 'storage'
          ? await uploadEmailImage(this.cityId, file)
          : await createEmbeddedEmailImage(file);
        if (this.editor.isDestroyed) return;

        const embeddedId = embeddedEmailImageId(image.source);
        if (embeddedId) {
          const images = { ...this.referencedImages(), [embeddedId]: image.dataUrl };
          const errors = validateEmbeddedEmailImages(images);
          if (errors.length) throw new Error(errors[0]);

          this.embeddedImages[embeddedId] = image.dataUrl;
        }

        this.imageCache.set(image.source, Promise.resolve(image.dataUrl));
        this.editor.chain().focus().setImage({ src: image.source, alt: file.name }).run();
      } catch (error) {
        this.error = error.message || 'Unable to insert the image.';
      } finally {
        this.uploading = false;
        if (!this.editor.isDestroyed) this.editor.setEditable(!this.disabled, false);
        this.$emit('busy', false);
        event.target.value = '';
      }
    }
  }
};
</script>

<style scoped>
.editor-toolbar {
  background: var(--bs-light);
}
.editor-select {
  width: auto;
  max-width: 200px;
}
:deep(.tiptap) {
  min-height: 300px;
  padding: 20px;
  outline: none;
  overflow-wrap: anywhere;
}
:deep(.tiptap p) {
  margin-bottom: 1rem;
}
:deep(.tiptap blockquote) {
  margin: 1rem 0;
  border: 1px solid #d8dee8;
  padding: 1rem;
}
:deep(.tiptap blockquote > p:last-child) {
  margin-bottom: 0;
}
:deep(.tiptap img) {
  display: block;
  max-width: 100%;
  height: auto;
  margin: 0 auto 20px;
}
:deep(.tiptap table) {
  width: 100%;
  border-collapse: collapse;
}
:deep(.tiptap th),
:deep(.tiptap td) {
  border: 1px solid var(--bs-border-color);
  padding: 10px;
  vertical-align: top;
}
:deep(.tiptap .selectedCell) {
  background: var(--bs-primary-bg-subtle, #e7f1ff);
}
:deep(.tiptap .ProseMirror-selectednode) {
  outline: 2px solid var(--bs-primary);
}
</style>
